import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, TrendingUp, AlertCircle, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface WarehouseStatusCardsProps {
  etlStatus: any[];
}

export const WarehouseStatusCards = ({ etlStatus }: WarehouseStatusCardsProps) => {
  const lastDimensionsRun = etlStatus.find(s => s.entity === 'dw.dimensions');
  const lastFactsRun = etlStatus.find(s => s.entity === 'dw.facts');
  const lastMartsRun = etlStatus.find(s => s.entity === 'amart.portfolio_performance');
  
  const getStatus = (run: any) => {
    if (!run) return { text: 'Never run', variant: 'secondary' as const };
    if (run.details?.error) return { text: 'Failed', variant: 'destructive' as const };
    return { text: 'Success', variant: 'default' as const };
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Dimensions ETL</CardTitle>
          <Database className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <Badge {...getStatus(lastDimensionsRun)}>{getStatus(lastDimensionsRun).text}</Badge>
            {lastDimensionsRun && (
              <>
                <p className="text-xs text-muted-foreground">
                  {formatTime(lastDimensionsRun.created_at)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {lastDimensionsRun.details?.results?.orgs || 0} orgs, 
                  {' '}{lastDimensionsRun.details?.results?.portfolios || 0} portfolios,
                  {' '}{lastDimensionsRun.details?.results?.products || 0} products
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Facts ETL</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <Badge {...getStatus(lastFactsRun)}>{getStatus(lastFactsRun).text}</Badge>
            {lastFactsRun && (
              <>
                <p className="text-xs text-muted-foreground">
                  {formatTime(lastFactsRun.created_at)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {lastFactsRun.details?.results?.accounts || 0} accounts,
                  {' '}{lastFactsRun.details?.results?.payments || 0} payments
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Marts Refresh</CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <Badge {...getStatus(lastMartsRun)}>{getStatus(lastMartsRun).text}</Badge>
            {lastMartsRun && (
              <>
                <p className="text-xs text-muted-foreground">
                  {formatTime(lastMartsRun.created_at)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {lastMartsRun.details?.duration_ms || 0}ms
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Last Error</CardTitle>
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {etlStatus.find(s => s.details?.error) ? (
              <>
                <Badge variant="destructive">Error</Badge>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {etlStatus.find(s => s.details?.error)?.details?.error}
                </p>
              </>
            ) : (
              <Badge variant="default">No errors</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
